import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Icon,
  Tooltip,
  Badge,
} from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import { createColumnHelper } from '@tanstack/react-table';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ExportButton from 'components/commonExport/ExportButton';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonSearch from 'components/commonSearchBar/CommonSearch';
import CommonTable from 'components/commonTable/CommonTable';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import { PiPencil } from 'react-icons/pi';
import {
  ListPriceStrategyData,
  SyncPriceStrategyMerchants,
} from 'reactRedux/thunks/priceStrategyThunk/priceStrategyThunk';
import useListState from 'hooks/useListState';
import SyncDataLoader from 'components/wholePageLoader/SyncDataLoader';
import SyncDataModal from 'components/popUpModel/SyncDataModal';
import {
  PRICE_STRATEGY_EXPORT_MATCH_TABLE_KEYS,
  sideBarRoutes,
} from 'utils/StaticData';

const PriceStrategyList = () => {
  const { tabletextColor } = useColorModeVariables();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isLoading, isSyncLoading, priceStrategyListData } = useSelector(
    (state) => state.PriceStrategySlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

  // Use the reusable hook for URL-synced filter state
  const { params, setParam, setParams, debouncedParams, page, setPage } =
    useListState({
      defaults: {
        search: '',
        sortKey: 'name',
        sortBy: 1,
        limit: 10,
      },
      debounceKeys: ['search'],
      debounceMs: 500,
    });

  // Fetch data on mount and when page/limit/search changes
  const getPriceStrategyList = useCallback(() => {
    dispatch(
      ListPriceStrategyData({
        page,
        limit: params.limit || 10,
        search: debouncedParams.search,
        sortKey: params.sortKey,
        sortBy: params.sortBy,
      }),
    );
  }, [
    dispatch,
    page,
    params.limit,
    debouncedParams.search,
    params.sortKey,
    params.sortBy,
  ]);

  useEffect(() => {
    getPriceStrategyList();
  }, [getPriceStrategyList]);

  // RAT-1677 AC-3: the export is only offered to Admin and Dealer. The route
  // already limits this page to those two, so this is defence in depth - the
  // backend answers 403 otherwise, and the app treats a 403 as an expired
  // session, which would log the user out instead of showing an error.
  const canExport = [sideBarRoutes?.Admin, sideBarRoutes?.Dealer].includes(
    adminDetails?.role,
  );

  // RAT-1677 AC-2: the same search and sort the list is showing, so the file
  // covers exactly the rows on screen. `sortKey` is deliberately not sent - the
  // list's only sort is by name, and the backend ignores sortKey entirely.
  const exportFilters = useMemo(
    () => ({
      search: debouncedParams.search,
      sortBy: params.sortBy,
    }),
    [debouncedParams.search, params.sortBy],
  );

  const columnHelper = createColumnHelper();

  // Get the data array from the API response
  const tableData = priceStrategyListData?.data || priceStrategyListData || [];
  const totalCount = priceStrategyListData?.total || 0;
  const getField = (row, ...keys) =>
    keys.reduce(
      (value, key) =>
        value !== undefined && value !== null ? value : row?.[key],
      undefined,
    );

  const columns = [
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          ID
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row.name, {
      id: 'name',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Name
        </Text>
      ),
      cell: (info) => {
        const name = info.getValue();
        const needsTooltip = name && name.length > 20;

        const content = (
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxW="130px"
          >
            {name || 'N/A'}
          </Text>
        );

        return needsTooltip ? (
          <Tooltip
            label={name}
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            px={3}
            py={2}
            borderRadius="md"
          >
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    }),
    columnHelper.accessor((row) => row.merchants, {
      id: 'merchants',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Merchants
        </Text>
      ),
      cell: (info) => {
        const merchants = info.getValue() || [];
        const count = info.row.original?.merchantCount ?? merchants.length;
        const returnUrl = `/admin/price-strategy${location.search}`;

        if (count === 0 || merchants.length === 0) {
          return (
            <Text fontSize="sm" fontWeight="600" color="gray.400">
              Unassigned
            </Text>
          );
        }

        if (count === 1) {
          return (
            <Link
              to={`/admin/merchants/details/${merchants[0].merchantId}`}
              state={{ returnUrl }}
              className="btn btn-primary rounded-2 p-1"
            >
              <Text
                color={tabletextColor}
                fontSize="sm"
                fontWeight="600"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                maxW="180px"
              >
                {merchants[0].name}
              </Text>
            </Link>
          );
        }

        // Multiple merchants — show first + "+N more" with tooltip listing all
        const allNames = merchants.map((m) => m.name).join(', ');
        const extraCount = count - 1;
        return (
          <Tooltip
            label={allNames}
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            px={3}
            py={2}
            borderRadius="md"
          >
            <Flex align="center" gap="6px" cursor="default">
              <Text
                color={tabletextColor}
                fontSize="sm"
                fontWeight="600"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                maxW="120px"
              >
                {merchants[0].name}
              </Text>
              <Badge colorScheme="blue" fontSize="xs" borderRadius="md">
                +{extraCount} more
              </Badge>
            </Flex>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor(
      (row) => getField(row, 'freeDuration', 'freeMinutes'),
      {
        id: 'freeMinutes',
        header: () => (
          <Text
            whiteSpace="nowrap"
            align="left"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
            fontFamily="Montserrat"
          >
            Free Duration
          </Text>
        ),
        cell: (info) => (
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
          >
            {info.getValue() ?? 'N/A'}
          </Text>
        ),
      },
    ),
    columnHelper.accessor((row) => getField(row, 'deposit', 'depositAmount'), {
      id: 'depositAmount',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Deposit
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => getField(row, 'priceMinute', 'priceTime'), {
      id: 'priceTime',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Time Interval
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row.price, {
      id: 'price',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Price per Interval
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor(
      (row) => getField(row, 'overtimeFees', 'timeoutAmount'),
      {
        id: 'timeoutAmount',
        header: () => (
          <Text
            whiteSpace="nowrap"
            align="left"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
            fontFamily="Montserrat"
          >
            Overtime Fees
          </Text>
        ),
        cell: (info) => (
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
          >
            {info.getValue() ?? 'N/A'}
          </Text>
        ),
      },
    ),
    columnHelper.accessor((row) => getField(row, 'maxPrice', 'dailyMaxPrice'), {
      id: 'dailyMaxPrice',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Daily Cap
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => getField(row, 'daysLimit', 'timeoutDay'), {
      id: 'timeoutDay',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Days Limit
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row.actions, {
      id: 'actions',
      header: () => (
        <Text
          justifyContent="space-between"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          width="80px"
          whiteSpace="nowrap"
        >
          ACTION
        </Text>
      ),
      cell: (row) => (
        <Flex gap={'10px'}>
          <Link
            to={`/admin/price-strategy/edit/${row?.row?.original?.id}`}
            className="btn btn-light rounded-3 p-1"
          >
            <PiPencil size={20} title="Edit" />
          </Link>
        </Flex>
      ),
    }),
  ];

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSyncData = () => {
    setIsSyncModalOpen(false);
    dispatch(
      SyncPriceStrategyMerchants(() => {
        setPage(1);
        getPriceStrategyList();
      }),
    );
  };

  return (
    <>
      {isSyncLoading && adminDetails?.role === sideBarRoutes?.Admin && (
        <SyncDataLoader />
      )}
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <Flex
          justify={{ base: 'flex-start', md: 'flex-end' }}
          mb={4}
          gap="20px"
          flexWrap={'wrap'}
        >
          <Box width="300px">
            <CommonSearch
              search={params.search}
              setSearch={(val) => setParam('search', val)}
              setPage={setPage}
            />
          </Box>
          {adminDetails?.role === sideBarRoutes?.Admin && (
            <Button
              backgroundColor={'red.500'}
              color="white"
              onClick={() => setIsSyncModalOpen(true)}
            >
              Sync Data
            </Button>
          )}
          <Link to={`/admin/price-strategy/add`}>
            <Button
              bg="reviveOrange"
              color="white"
              borderRadius="12px"
              _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
            >
              <Icon as={FaPlus} width="15px" height="15px" marginRight="5px" />
              Add Price Strategy
            </Button>
          </Link>
          {canExport && (
            <ExportButton
              entity="price-strategy"
              title="Export Price Strategies"
              currentFilters={exportFilters}
              filteredTotal={totalCount}
              matchTableKeys={PRICE_STRATEGY_EXPORT_MATCH_TABLE_KEYS}
            />
          )}
        </Flex>

        <CommonTable
          columns={columns}
          data={tableData}
          keyValue="id"
          isLink={true}
          isLoading={isLoading}
          parentLink={'/admin/price-strategy/details'}
          rowclick={['actions', 'merchants']}
          isSorting={true}
          excludes={['name']}
          getfilter={(e) => setParams({ sortKey: e.sortKey, sortBy: e.sortBy })}
          defaultFilter={{ sortKey: params.sortKey, sortBy: params.sortBy }}
          dynamicWidth={{
            id: '80px',
            name: '150px',
            merchants: '220px',
            freeMinutes: '100px',
            depositAmount: '90px',
            priceTime: '100px',
            price: '130px',
            timeoutAmount: '110px',
            dailyMaxPrice: '90px',
            timeoutDay: '80px',
            actions: '70px',
          }}
        />
        <CommonPagination
          totalCount={totalCount}
          activePage={page}
          pageCount={Math.ceil(totalCount / (params.limit || 10)) || 1}
          onPageChange={handlePageChange}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
      </Box>

      {isSyncModalOpen && (
        <SyncDataModal
          isOpen={isSyncModalOpen}
          onClose={() => setIsSyncModalOpen(false)}
          title="Are you sure you want to sync merchant migration data for price strategy?"
          SolidBTNText="Sync"
          onClick={handleSyncData}
          loader={isSyncLoading}
        />
      )}
    </>
  );
};

export default PriceStrategyList;
